import { Box, Portal } from '@chakra-ui/react';
import { get } from 'lodash';
import { DateTime } from 'luxon';
import React, { lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Flex } from '../../components/UI-CHAKRA/Flex';
import withSuspense from '../../components/withLazyComponent';
import { HEADER_HEIGHT, HEADER_HEIGHT_MOBILE } from '../../contants/size';
import { useIsMobile } from '../../helpers/useIsMobile';
import { useDrawerStore } from '../../stores/drawerStore';
import { usePrescriptionLayoutStore } from '../../stores/prescriptionLayoutStore';
import { usePrescriptionStore } from '../../stores/prescriptionStore';
import News from '../my-central/Prescriber/News';
import { MENUS } from '../my-central/constants';
import { ModelFeedbackToast } from '../my-central/Models/ModelFeedbackToast';
import ConsentDialog from './ConsentDialog/ConsentDialog';
import PrescriptionFooter from './PrescriptionFooter';
import PrescriptionHeader from './PrescriptionHeader/PrescriptionHeader';
import HamburgerMenuPanel from './HamburgerMenuPanel/HamburgerMenuPanel';
import { UserSignUpResponse } from './service';
import { TrackerEventService } from './trackerEventService';
import { APP_VIEWPORT_HEIGHT } from '../../helpers/embeddedViewport';

const ContainerWithRef = styled(Box)`
  &::-webkit-scrollbar {
    width: 9px;
  }
`;

interface PrescriptionLayoutProps {
  continueButtonLabel: string;
  cancelButtonLabel?: string;
  children: React.ReactNode;
  onCancel?(): void;
  onContinue(): void;
  loading: boolean;
  showMyCentral: boolean;
  continueDisabled?: boolean;
  continueTooltip?: React.ReactNode;
  showMenu?: boolean;
  hideHeader?: boolean;
  userSignUpStatus?: UserSignUpResponse | null;
  footerLeftAction?: React.ReactNode;
}

const SncrNumberingPanel = withSuspense(lazy(() => import('./SncrNumbering/SncrNumberingPanel')));

function PrescriptionLayout(props: PrescriptionLayoutProps) {
  const rootContainerRef = useRef(null);

  const { prescription } = usePrescriptionStore();
  const { prescriberConfiguration } = usePrescriptionStore();
  const { drawerOpen, setDrawerOpen, selectedMenu, setSelectedMenu, setOfficeTriggerSource, openOfficeAutomatically } = useDrawerStore();
  const { setScrollTop } = usePrescriptionLayoutStore();

  const [newsAreOpen, setNewsAreOpen] = useState(false);
  const [canShowConsentDialog, setCanShowConsentDialog] = useState(false);
  const hasOpenedAutomatically = useRef(false);

  const isMobile = useIsMobile();

  useEffect(() => {
    if (prescription && prescription.idPrescricao && prescription.Prescritor && prescription.Prescritor.idPrescritor) {
      setCanShowConsentDialog(true);
    } else {
      setCanShowConsentDialog(false);
    }
  }, [prescription]);

  useEffect(() => {
    if (props.userSignUpStatus?.enabled && props.userSignUpStatus?.data?.shouldOpenModal) {
      hasOpenedAutomatically.current = true;
      openOfficeAutomatically();
    }
  }, [props.userSignUpStatus, openOfficeAutomatically]);

  useEffect(() => {
    if (hasOpenedAutomatically.current) {
      hasOpenedAutomatically.current = false;
      return;
    }
    if (!drawerOpen && selectedMenu !== MENUS.OFFICE) {
      setOfficeTriggerSource('tab_click');
    }
  }, [drawerOpen, selectedMenu, setOfficeTriggerSource]);

  const canUsePrescriptionHistory =
    get(prescriberConfiguration, 'Features.useCookieAuth', false) ||
    get(prescriberConfiguration, 'Features.canUseHistoricWithBasic', false);

  const onContinue = useCallback(() => {
    props.onContinue();
    setDrawerOpen(false);
  }, [setDrawerOpen, props]);

  const containerHeight = useMemo(() => {
    if (props.hideHeader) return APP_VIEWPORT_HEIGHT;
    if (isMobile) return `calc(${APP_VIEWPORT_HEIGHT} - ${HEADER_HEIGHT_MOBILE})`;
    return `calc(${APP_VIEWPORT_HEIGHT} - ${HEADER_HEIGHT})`;
  }, [isMobile, props.hideHeader]);

  const handleScrollTop = useCallback(
    (e: Event) => {
      const targetDiv: Element = e.target as Element;
      setScrollTop(targetDiv.scrollTop);
    },
    [setScrollTop]
  );

  useEffect(() => {
    const container = document.getElementById('Container');
    if (!container) return;
    container.addEventListener('scroll', handleScrollTop);
    return () => {
      container.removeEventListener('scroll', handleScrollTop);
    };
  }, [handleScrollTop]);

  useEffect(() => {
    if (!isMobile) return;
    const handleFocus = (e: any) => {
      const focusedElement = e.target;
      if (focusedElement && (focusedElement.tagName === 'INPUT' || focusedElement.tagName === 'TEXTAREA')) {
        setTimeout(() => {
          focusedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
      }
    };
    document.addEventListener('focus', handleFocus, true);
    return () => {
      document.removeEventListener('focus', handleFocus, true);
    };
  }, [isMobile]);

  const trackEventClickedNews = async () => {
    if (prescription)
      await TrackerEventService.track(prescription.idPrescricao, 'backoffice_prescription_icons_clicou', {
        idPrescricao: prescription?.idPrescricao,
        idMedico: prescription?.idPrescritor,
        codigoValidacao: prescription?.CodigoValidacao,
        idParceiro: prescriberConfiguration?.idParceiro,
        categoria: 'novidades',
        timeStamp: DateTime.now(),
      });
  };

  return (
    <Flex direction="column" h={APP_VIEWPORT_HEIGHT} maxH={APP_VIEWPORT_HEIGHT} overflow="hidden">
      <ModelFeedbackToast />
      {!props.hideHeader && (
        <Box style={{ padding: 0 }}>
          <PrescriptionHeader showMenu={props.showMenu !== false && props.showMyCentral} />
        </Box>
      )}

      <Flex flex="1" minH={0} id="PrescriptionContent" backgroundColor={'#f8f9fa'} position={'relative'} padding={0}>
        <Flex direction="column" flex="1" width="100%" position="relative">
          <ContainerWithRef
            id="Container"
            position={'relative'}
            flex={'0 1 auto'}
            overflowY="auto"
            height={containerHeight}
            width="100%"
            ref={rootContainerRef as any}
            paddingBottom={'0px'}
          >
            {props.children}
          </ContainerWithRef>
          <PrescriptionFooter
            onCancel={props.onCancel}
            onContinue={onContinue}
            continueLabel={props.continueButtonLabel}
            loading={props.loading}
            continueDisabled={props.continueDisabled}
            continueTooltip={props.continueTooltip}
            cancelLabel={props.cancelButtonLabel || 'Cancelar'}
            leftAction={props.footerLeftAction}
          />
        </Flex>
      </Flex>

      {
        props.showMyCentral && (
          <Portal>
            <HamburgerMenuPanel
              canUsePrescriptionHistory={canUsePrescriptionHistory}
              userSignUpStatus={props.userSignUpStatus}
            />
          </Portal>
        )
      }

      {
        canShowConsentDialog && prescription && prescription.Prescritor && (
          <Portal>
            <ConsentDialog
              doctorId={prescription.Prescritor.idPrescritor}
              prescriberEmail={prescription.Prescritor.Email}
              prescriptionId={prescription.idPrescricao}
            />
          </Portal>
        )
      }

      {
        selectedMenu === MENUS.News && newsAreOpen && (
          <Portal>
            <News
              onClose={() => {
                setNewsAreOpen(false);
                setDrawerOpen(false);
                setSelectedMenu('');
              }}
            />
          </Portal>
        )
      }
      {
        selectedMenu === MENUS.SNCR_NUMBERING && (
          <Portal>
            <SncrNumberingPanel
              onClose={() => {
                setSelectedMenu('');
                setDrawerOpen(false);
              }}
              onBack={() => setSelectedMenu('')}
            />
          </Portal>
        )
      }
    </Flex >
  );
}

export default React.memo(PrescriptionLayout);
